import React, { MouseEventHandler, useState } from "react";
import { Button } from "react-bootstrap";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useTheme } from "checkout/ts/hooks/useButtonStyles";
import { StateShape } from "checkout/ts/redux/store";
import { isTestCafe, isTestProcessingMode } from "checkout/ts/utils/environment";
import classNames from "classnames";
import { LOCALE_LABELS } from "common/locale/labels";
import { isDevelop } from "common/utils/environment";
import { ProcessingMode } from "univapay-node";

import { DialogButton } from "./DialogButton";

/*
 * This component is designed to behave like a modal dialog for confirming user acknowledgement of test environment.
 * However, it is not implemented as a true modal because Bootstrap does not support nested dialogs.
 * Since this needs to be displayed inside the widget modal, we use a custom overlay approach instead.
 */
export const TestEnvironmentConfirmationWindow = () => {
    const configuration = useSelector((state: StateShape) => state.configuration);
    const { mode } = configuration?.data || {};
    const { formatMessage } = useIntl();
    const [confirmed, setConfirmed] = useState(false);
    const dark = useTheme();

    const {
        params: { hideTestBanner },
    } = useSelector((state: StateShape) => state.application.params);

    const formattedMode = (() => {
        switch (mode) {
            case ProcessingMode.LIVE_TEST:
                return formatMessage({ id: LOCALE_LABELS.GLOBAL_TEST_ENVIRONMENT_BANNER_LIVE_TEST_MODE });
            case ProcessingMode.TEST:
                return formatMessage({ id: LOCALE_LABELS.GLOBAL_TEST_ENVIRONMENT_BANNER_TEST_MODE });
            default:
                return "";
        }
    })();

    const handleButtonClick: MouseEventHandler<Button> = (event) => {
        setConfirmed(true);
        event.stopPropagation();
    };

    const displayConfirmationWindow =
        (!isDevelop() || isTestCafe) && isTestProcessingMode(mode) && !confirmed && !hideTestBanner;

    if (!displayConfirmationWindow) {
        return null;
    }

    return (
        <div className={classNames("test-environment-confirmation-window", { "dark": dark })}>
            <div className="popup">
                <p data-name="test-environment-confirmation-text">
                    {formatMessage(
                        { id: LOCALE_LABELS.GLOBAL_ENVIRONMENT_MODE_CONFIRMATION_MODAL_MESSAGE },
                        { mode: formattedMode }
                    )}
                </p>

                <DialogButton
                    data-name="test-environment-confirmation-button"
                    className={classNames("dialog-button", { "dark": dark })}
                    onClick={handleButtonClick}>
                    {formatMessage({ id: LOCALE_LABELS.GLOBAL_ENVIRONMENT_MODE_CONFIRMATION_MODAL_BUTTON })}
                </DialogButton>
            </div>
        </div>
    );
};
